/**
 * Similar Products popup controller.
 * Load this once in assets/similar-products-modal.js.
 */
class SimilarProductsModal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.content = this.querySelector('[data-similar-products-modal-content]');
    this.dialog = this.querySelector('[data-similar-products-dialog]');
    this.lastTrigger = null;
    this.abortController = null;

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleKeydown);
    this.abortController?.abort();
  }

  handleDocumentClick = (event) => {
    const trigger = event.target.closest('[data-similar-products-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-similar-products-close]') || event.target === this.dialog) {
      this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('open')) this.close();
  };

  async open(trigger) {
    const productId = trigger.dataset.productId;
    if (!productId) return;

    this.lastTrigger = trigger;
    this.setAttribute('open', '');
    document.documentElement.classList.add('similar-products-open');
    this.querySelector('[data-similar-products-close]')?.focus();
    this.showLoading();

    this.abortController?.abort();
    this.abortController = new AbortController();

    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    // A configured section ID lets Shopify render the theme's complete product-card blocks.
    url.searchParams.set('section_id', this.dataset.sectionId || 'similar-products-modal');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('intent', 'related');
    url.searchParams.set('limit', trigger.dataset.similarLimit || '8');

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const recommendationContent = documentFragment.querySelector('[data-similar-products-content]');
      if (!recommendationContent) throw new Error('Similar-products section markup was not returned');

      this.content.replaceChildren(...recommendationContent.cloneNode(true).childNodes);
      this.content.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.content.innerHTML = '<p class="similar-products-modal__message">Similar products could not be loaded. Please try again.</p>';
      this.content.removeAttribute('aria-busy');
      console.error('Similar products:', error);
    }
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.documentElement.classList.remove('similar-products-open');
    this.abortController?.abort();
    this.lastTrigger?.focus();
  }

  showLoading() {
    this.content.setAttribute('aria-busy', 'true');
    this.content.innerHTML = '<p class="similar-products-modal__message">Loading similar products…</p>';
  }
}

if (!customElements.get('similar-products-modal')) {
  customElements.define('similar-products-modal', SimilarProductsModal);
}
